const commonNotesTooltip = 'Note facoltative.';
const catalogTooltip = (catalogName: string) =>
  `Usa valori predefiniti (Catalogo: ${catalogName}), modificabili in Impostazioni -> Valori predefiniti.`;

export const companyFieldTooltips = {
  code: "Codice usato per identificare internamente l'azienda.",
  address: 'Indirizzo completo della sede legale (via, numero, CAP, comune, provincia).',
  notes: commonNotesTooltip,
} as const;

export const siteFieldTooltips = {
  address: 'Indirizzo completo dello stabilimento (via, numero, CAP, comune, provincia).',
  periodicity:
    'Frequenza programmata dei sopralluoghi per questo stabilimento, usata per calcolare il mese di controllo.',
  contractActivationDate:
    'Mese/anno di attivazione del contratto di servizio (mese di attivazione direzione tecnica), usato per calcolare il mese di controllo.',
  cpiPresent:
    'Indica se lo stabilimento dispone del Certificato di Prevenzione Incendi (CPI) in corso di validita.',
  cpiProtocolNumber: 'Numero di protocollo CPI riportato nel documento.',
  cpiLastRenewalDate:
    'Data ultimo rinnovo del CPI (Certificato di Prevenzione Incendi). Obbligatoria se CPI presente.',
  cpiExpiryDate:
    'Data di scadenza del CPI (Certificato di Prevenzione Incendi). Obbligatoria se CPI presente.',
  ammoniaActivityCodes:
    "Indica se i codici ATECO dello stabilimento includono attivita con ammoniaca; riportato nel verbale come 'Presenti codici attivita'.",
  authorizationProtocolNumber: "Numero di protocollo dell'autorizzazione riportato nel documento.",
  authorizationIssueDate: "Data di rilascio dell'autorizzazione.",
  authorizationExpiryDate: "Data di scadenza dell'autorizzazione.",
  authorizedNh3Kg: 'Quantita di ammoniaca autorizzata (kg).',
  officialGazettePublished:
    'Se il provvedimento autorizzativo e pubblicato in Gazzetta Ufficiale (G.U.).',
  officialGazetteDate:
    'Data di pubblicazione in Gazzetta Ufficiale (G.U.). Obbligatoria se la pubblicazione e indicata.',
  authorizationPrescriptions:
    "Prescrizioni specifiche dell'autorizzazione. Eventuali obblighi, limitazioni o condizioni imposti dall'ente autorizzatore.",
  reperibilePhone:
    'Numero di telefono per reperibilita dello stabilimento. Usato come contatto urgente nel verbale.',
  notes: commonNotesTooltip,
} as const;

export const personnelFieldTooltips = {
  scope:
    'Ambito di responsabilita: se azienda intera compare in tutti i verbali, se stabilimento specifico solo nei report di quel sito.',
  siteId:
    "Stabilimento associato quando l'ambito e specifico. Obbligatorio se l'ambito e Stabilimento.",
  email:
    "Indirizzo email per comunicazioni e invio verbali. Obbligatoria se 'Recapito invio verbali' e attivo.",
  phone:
    "Telefono diretto o reperibile della persona. Obbligatorio se 'Riferimento in caso di allarme' o 'Reperibile' e attivo.",
  reportRecipient: 'Riceve copia dei verbali di sopralluogo.',
  alarmContact:
    'Contatto da avvisare in caso di allarme o emergenza; compare nel verbale come riferimento.',
  reperibile: 'Persona disponibile per reperibilita; usata per i recapiti reperibili nel verbale.',
  fireTraining: 'Formazione antincendio completata, riportata nel verbale.',
  firstAidTraining: 'Formazione primo soccorso completata, riportata nel verbale.',
  licensed:
    "Possiede la patente di abilitazione all'impiego dei gas tossici (conservazione, custodia, trasporto, impiego); riportato nel verbale.",
  licenseExpiry:
    'Data di scadenza della patente, riportata nel verbale. Obbligatoria se Patentato e attivo.',
  licenseLastUpdate:
    'Data ultimo rinnovo o aggiornamento della patente, riportata nel verbale. Obbligatoria se Patentato e attivo.',
} as const;

export const unitFieldTooltips = {
  general: {
    name: "Nome identificativo dell'impianto.",
    location: `Ubicazione dell'impianto all'interno dello stabilimento. ${catalogTooltip(
      'Localizzazioni Impianto'
    )}`,
    reperibilePhone:
      'Numero da chiamare per reperibilita a turno quando serve un contatto immediato.',
    toxicGasType: `Gas tossico rilevato o gestito dall'impianto. ${catalogTooltip('Gas Tossici')}`,
    impiantoType: `Tipologia di impianto frigorifero. ${catalogTooltip('Tipi Impianto')}`,
    ammoniaKg: 'Carica totale di ammoniaca presente (kg).',
  },
  components: {
    type: `Tipologia del componente principale installato. ${catalogTooltip(
      'Categorie Componenti'
    )}`,
    customLabel: 'Descrizione personalizzata del componente.',
    capacityKw: 'Potenza o capacita nominale del componente (kW).',
    location: `Posizione del componente nell'impianto. ${catalogTooltip(
      'Localizzazioni Impianto'
    )}`,
    notes: commonNotesTooltip,
  },
  detection: {
    detectorsToxicCount: 'Numero di rilevatori per gas tossici.',
    detectorsExplosiveCount: 'Numero di rilevatori per gas esplosivi.',
    detectorPositions: 'Posizioni principali dei rilevatori.',
    detectorsCeilingCount: 'Rilevatori installati in quota soffitto.',
    detectorsHumanCount: 'Rilevatori installati in quota uomo.',
    thresholdType: `Tipologia della soglia (tossicita o esplosivita). ${catalogTooltip(
      'Tipi soglia allarme'
    )}`,
    thresholdUnit: `Unita di misura della soglia (ppm, % LIE, ecc.). ${catalogTooltip(
      'Unita soglia allarme'
    )}`,
    thresholdValue: 'Valore numerico della soglia di intervento.',
    thresholdActions: `Azioni che si attivano al superamento soglia. ${catalogTooltip(
      'Azioni soglia allarme'
    )}`,
    calibrationNotes: commonNotesTooltip,
    detectionMaintenanceName: 'Ragione sociale del manutentore rilevazione.',
    detectionMaintenanceNotes: commonNotesTooltip,
  },
  discharge: {
    vsReliefCollectorsCount: 'Numero di collettori di scarico.',
    vsReliefSizing: 'Norma o criterio di dimensionamento.',
    abatementVolumeMc: 'Volume totale della vasca di abbattimento.',
    abatementMinWaterMc: 'Volume minimo di acqua disponibile.',
    abatementMinFreeMc: 'Volume libero disponibile sopra il livello acqua.',
    abatementLocation: 'Ubicazione della vasca (interrata o fuori terra).',
    abatementSignage: 'Segnaletica presente in area vasca.',
    abatementAccessibility: 'Accessibilita per ispezione e manutenzione.',
    abatementMaterial: 'Materiale costruttivo della vasca.',
    abatementOrientation: 'Orientamento prevalente della vasca.',
    abatementDimensions: 'Dimensioni complessive della vasca.',
    abatementLevelInstrumentationPresent: 'Presenza di strumentazione per il livello.',
    abatementLevelInstrumentationType: 'Tipologia strumentazione di livello.',
    abatementContinuousPhMonitoring: 'Monitoraggio continuo del pH.',
    abatementEmergencyPreferredLine: 'Presenza linea preferenziale di emergenza.',
    abatementEmergencyGenerator: 'Presenza generatore di emergenza.',
    abatementEmergencyUps: 'Presenza UPS per emergenza.',
    vvfDocs: 'Stato documentazione VVF (prevista o non prevista).',
    vvfDocsLocation: 'Ubicazione della documentazione VVF.',
  },
  ventilation: {
    ventilationType: 'Tipologia di ventilazione principale.',
    ventilationFlowRateM3h: "Portata d'aria totale (m3/h).",
    ventilationNotes: commonNotesTooltip,
    ventilationNaturalHighM2: 'Superficie aerazione naturale alta (m2).',
    ventilationNaturalLowM2: 'Superficie aerazione naturale bassa (m2).',
    ventilationNaturalNotes: commonNotesTooltip,
    ventilationExtractUnits: 'Numero di estrattori presenti.',
    ventilationExtractLogic: 'Logica di attivazione estrattori.',
    ventilationExtractMode: 'Modalita di esercizio degli estrattori.',
    ventilationExtractAtex: 'Estrattori conformi ATEX.',
    ventilationExtractFlowM3h: 'Portata estrattori (m3/h).',
    ventilationExtractOperation: 'Note sul funzionamento estrattori.',
    scrubberPresent: 'Presenza di scrubber per abbattimento. Verificabile in campo.',
    scrubberNotes: commonNotesTooltip,
    scrubberAirFlowM3h:
      'Portata aria scrubber (m3/h). Dato documentale - non verificabile direttamente in campo.',
    scrubberWaterFlowM3h:
      'Portata acqua scrubber (m3/h). Dato documentale - non verificabile direttamente in campo.',
    scrubberWaterFlowSpec: 'Specifiche o fonte portata acqua. Dato documentale.',
    scrubberContactTimeSeconds:
      'Tempo di contatto (secondi). Dato documentale - da specifiche di progetto.',
    scrubberWaterSupply: 'Alimentazione idrica dello scrubber. Verificabile in campo.',
    scrubberWasteCollection: 'Gestione raccolta reflui scrubber. Verificabile in campo.',
    scrubberAtex: 'Scrubber conforme ATEX. Verificabile tramite targhetta/certificazione.',
  },
  safety: {
    emergencyDialerPresent: 'Presenza combinatore/dialer emergenza.',
    emergencyGeneratorPresent: 'Presenza generatore emergenza.',
    emergencyGuardServicePresent: 'Presenza guardiania o presidio.',
    emergencyNotes: commonNotesTooltip,
    abatementSystemPresent: 'Presenza sistema di abbattimento.',
    abatementSystemControlMode: 'Modalita di controllo del sistema.',
    abatementSystemTrim: 'Presenza trim nel sistema di abbattimento.',
    abatementSystemLimitSwitches: 'Presenza finecorsa nel sistema.',
    abatementSystemType: 'Tipologia del sistema di abbattimento.',
    abatementSystemSupply: 'Alimentazione del sistema di abbattimento.',
    abatementSystemDisposal: 'Scarico del sistema di abbattimento.',
    abatementSystemWaterFlowM3h: 'Portata acqua del sistema (m3/h).',
    emergencyEeTripButtons: 'Pulsanti di sgancio elettrico presenti.',
    emergencyEeTripDetector: 'Sgancio automatico collegato a rilevazione.',
    emergencyElettroserraturaPresent: 'Elettroserrature presenti.',
    emergencyLightingPresent: 'Illuminazione di emergenza presente.',
    emergencyLightingAtex: 'Illuminazione emergenza conforme ATEX.',
    sgancioAutomaticoPresent: 'Sgancio automatico presente.',
    sogliaSgancioAutomatico: 'Soglia di attivazione sgancio automatico.',
    pulsanteSgancioPresent: 'Pulsante di sgancio presente.',
    powerPreferredLine: 'Linea preferenziale disponibile.',
    powerGenerator: 'Generatore di emergenza disponibile.',
    powerUps: 'UPS di emergenza disponibile.',
    powerNotes: commonNotesTooltip,
    valvesReliefPressures: 'Pressioni di taratura valvole di sicurezza.',
    valvesReliefPeriodicity: 'Periodicita verifiche valvole (anni).',
    valvesReliefNotes: commonNotesTooltip,
    valvesIsolationPresent: 'Valvole intercettazione presenti.',
    valvesIsolationDiverter: 'Presenza deviatori/intercettazioni specifiche.',
    valvesIsolationNotes: commonNotesTooltip,
    btGreaseBand: 'Presenza della benda grassa BT prevista.',
  },
  maintenance: {
    maintenanceName: "Ragione sociale dell'azienda manutentrice.",
    maintenanceNotes: commonNotesTooltip,
  },
  notes: {
    notes: commonNotesTooltip,
  },
} as const;
